import { Controller } from '@hotwired/stimulus'

const ACTIVE_TAB_CLASSES = [
  'border-brand-200',
  'bg-white',
  'text-brand-900',
  'shadow-sm',
  'ring-1',
  'ring-brand-100',
  'lg:border-l-brand-600'
]

const INACTIVE_TAB_CLASSES = [
  'border-transparent',
  'text-slate-600',
  'hover:bg-white/80',
  'hover:text-slate-900',
  'lg:border-l-transparent'
]

const ACTIVE_ICON_CLASSES = ['bg-brand-50', 'text-brand-700', 'ring-brand-200']
const INACTIVE_ICON_CLASSES = ['bg-white', 'text-slate-500', 'ring-slate-200/80']

export default class extends Controller {
  static targets = ['tab', 'panel', 'tabInput']
  static values = { active: { type: String, default: 'branding' } }

  connect () {
    this.show(this.initialTab(), { updateUrl: false })
    this.syncUrl(this.activeValue)
  }

  select (event) {
    event.preventDefault()
    this.show(event.currentTarget.dataset.tabId)
  }

  initialTab () {
    const tab = new URL(window.location.href).searchParams.get('tab')
    if (tab && this.isValidTab(tab)) return tab
    if (this.activeValue && this.isValidTab(this.activeValue)) return this.activeValue
    return 'branding'
  }

  isValidTab (id) {
    return this.tabTargets.some((tab) => tab.dataset.tabId === id)
  }

  show (id, { updateUrl = true } = {}) {
    if (!this.isValidTab(id)) id = 'branding'

    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.tabId === id
      ACTIVE_TAB_CLASSES.forEach((cls) => tab.classList.toggle(cls, active))
      INACTIVE_TAB_CLASSES.forEach((cls) => tab.classList.toggle(cls, !active))
      tab.setAttribute('aria-selected', active ? 'true' : 'false')

      const icon = tab.querySelector('[data-settings-tabs-icon]')
      if (icon) {
        ACTIVE_ICON_CLASSES.forEach((cls) => icon.classList.toggle(cls, active))
        INACTIVE_ICON_CLASSES.forEach((cls) => icon.classList.toggle(cls, !active))
      }
    })

    this.panelTargets.forEach((panel) => {
      panel.classList.toggle('hidden', panel.dataset.tabId !== id)
    })

    if (this.hasTabInputTarget) {
      this.tabInputTarget.value = id
    }

    this.activeValue = id

    if (updateUrl) {
      this.syncUrl(id)
    }

    document.dispatchEvent(new CustomEvent('settings-tabs:shown', { detail: { tabId: id } }))
  }

  syncUrl (id) {
    const url = new URL(window.location.href)
    url.hash = ''

    if (id === 'branding') {
      url.searchParams.delete('tab')
    } else {
      url.searchParams.set('tab', id)
    }

    window.history.replaceState({}, '', url)
  }
}
